import type { FieldValues } from 'react-hook-form';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Turnstile from 'react-turnstile';

import type { CaptchaResponse, CaptchaWidgetProps } from '../index';

const MAX_RETRY_ATTEMPTS = 3; // Define a maximum number of retries

const AuthChallengeWidget = <T extends FieldValues = FieldValues>({
  config,
  name,
  rules,
  control,
  setError,
  clearErrors,
  onCaptchaResponse,
  theme,
  error,
}: CaptchaWidgetProps<T>) => {
  const retryCount = useRef(0);
  const { t } = useTranslation('common');

  // Sync external Auth0 server error into react-hook-form
  useEffect(() => {
    if (error && setError) {
      setError(name, { type: 'server', message: error });
    }
  }, [error, name, setError]);

  if (config.provider !== 'auth0_v2') {
    return null;
  }

  const siteKey = config.siteKey;

  if (!siteKey) {
    return null;
  }

  const handleVerify = (token: string, onChange: (value: string) => void) => {
    retryCount.current = 0;
    clearErrors?.(name); // Clear all captcha errors on success

    // Update react-hook-form state
    onChange(token);

    const response: CaptchaResponse = {
      provider: 'auth0_v2',
      token: token,
    };
    onCaptchaResponse(response);
  };

  const handleTurnstileError = (
    turnstileError: string,
    onChange: (value: string) => void,
  ) => {
    // Clear the form value on error
    onChange('');
    onCaptchaResponse(null);

    if (retryCount.current < MAX_RETRY_ATTEMPTS) {
      retryCount.current += 1;
      return;
    }

    // Set widget error via react-hook-form after max retries
    const translation = t('common:captcha.errorTurnstile');
    setError?.(name, {
      type: 'widget',
      message: `${translation} ${turnstileError}`,
    });
  };

  const handleExpire = (onChange: (value: string) => void) => {
    // Clear the form value on expiration
    onChange('');
    onCaptchaResponse(null);
    clearErrors?.(name); // Clear errors on expiration (will retry)
    retryCount.current = 0;
  };

  const auth0CaptchaWidgetStyle = {
    transformOrigin: '0 0',
    overflow: 'hidden',
    display: 'inline-block',
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange }, fieldState }) => (
        <div>
          <div style={auth0CaptchaWidgetStyle}>
            <Turnstile
              sitekey={siteKey}
              onVerify={(token) => handleVerify(token, onChange)}
              onError={(err) => handleTurnstileError(err, onChange)}
              onExpire={() => handleExpire(onChange)}
              theme={theme}
              size="normal"
            />
          </div>
          {fieldState.error?.message && (
            <StatusMessage className="u-margin-t-xs" mode="error">
              {fieldState.error.message}
            </StatusMessage>
          )}
        </div>
      )}
    />
  );
};

export default AuthChallengeWidget;
